import { useCallback, useMemo } from "react"

import {
  matchStatusField,
  needsActionField,
  stampMatchStatus,
  useMatchStatusesForFilter,
} from "~/components/demand-2/shared/matchStatusFilter"
import { insightLabelFor } from "~/components/demand-2/shared/noosBreakdown"
import {
  REVIEW_FILTER_FIELD,
  stampReviews,
  useReviewsForFilter,
} from "~/components/demand-2/shared/reviewFilter"
import { useFraming } from "~/components/demand-2/useDemandIntents"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { usePageFilteredVariants } from "~/hooks/useVariants"
import {
  ownVariantAnswers,
  VARIANT_ANSWER_QUESTIONS,
  type FramingQuestionId,
  type FramingVariantAnswerRow,
} from "~/utils/demandFraming"
import type { NoosMetricKey } from "~/utils/demandInsightValues"
import type { Filter, FilterFieldType } from "~/utils/filter"

/**
 * Page-filter field naming the insights a variant answers on its own. Not a column of the variant
 * read (answers live in `DemandFramingAnswer`), so tabs stamp it on before their page filters run.
 */
const OWN_INSIGHT_ANSWERS_FIELD = "ownInsightAnswers"

/** The insight's row label, so the chip reads like the row it came from. */
const ownInsightAnswerLabel = (question: FramingQuestionId): string =>
  insightLabelFor(question as NoosMetricKey)

/** Module-level, so the filter bar's field list stays stable. */
const OWN_INSIGHT_ANSWERS_FILTER_FIELDS: FilterFieldType[] = [
  {
    value: OWN_INSIGHT_ANSWERS_FIELD,
    name: "Own insight answer",
    type: "enum",
    options: VARIANT_ANSWER_QUESTIONS.map(ownInsightAnswerLabel),
  },
]

/** `unscoped`: Performance outside a collection (portfolio, product page), where rows mix kinds. */
export type DemandFilterPage = "demand" | "assortment" | "performance" | "unscoped"

const UNSCOPED_FILTER_FIELDS = [REVIEW_FILTER_FIELD, matchStatusField("noos"), needsActionField]

/**
 * The page-filter fields each demand tab offers. Every tab keeps its own filters, so on a collection
 * Review lives on Demand and Match status on Assortment only.
 */
export const DEMAND_PAGE_FILTER_FIELDS: Record<
  DemandFilterPage,
  Record<"fashion" | "noos", FilterFieldType[]>
> = {
  demand: {
    fashion: [...OWN_INSIGHT_ANSWERS_FILTER_FIELDS, REVIEW_FILTER_FIELD],
    noos: [REVIEW_FILTER_FIELD],
  },
  assortment: {
    fashion: [...OWN_INSIGHT_ANSWERS_FILTER_FIELDS, matchStatusField("fashion"), needsActionField],
    noos: [matchStatusField("noos"), needsActionField],
  },
  performance: {
    fashion: OWN_INSIGHT_ANSWERS_FILTER_FIELDS,
    noos: [],
  },
  unscoped: {
    fashion: UNSCOPED_FILTER_FIELDS,
    noos: UNSCOPED_FILTER_FIELDS,
  },
}

export const ownInsightAnswerFilter = (question: FramingQuestionId): Filter => ({
  field: OWN_INSIGHT_ANSWERS_FIELD,
  operator: "oneOf",
  value: "",
  values: [ownInsightAnswerLabel(question)],
  by: "variantId",
})

export const stampOwnInsightAnswers = <V extends IVariantSummary>(
  variants: readonly V[],
  variantAnswers: readonly FramingVariantAnswerRow[],
): (V & { [OWN_INSIGHT_ANSWERS_FIELD]: string[] })[] => {
  const byVariant = new Map<string, string[]>()
  for (const a of variantAnswers) {
    const labels = byVariant.get(a.variantId) ?? []
    labels.push(ownInsightAnswerLabel(a.question))
    byVariant.set(a.variantId, labels)
  }
  return variants.map((v) => ({
    ...v,
    [OWN_INSIGHT_ANSWERS_FIELD]: byVariant.get(v.variantId) ?? [],
  }))
}

/**
 * Stamps every field in `DEMAND_PAGE_FILTER_FIELDS`, whichever tab offers it. The review and
 * match-status stamps leave rows untouched until their filter is set, or `matchStatus` asks for it.
 */
export const useStampDemandPageFields = ({
  matchStatus = false,
}: { matchStatus?: boolean } = {}) => {
  const framing = useFraming()
  const variantAnswers = useMemo(() => ownVariantAnswers(framing), [framing])
  const reviews = useReviewsForFilter()
  const matchStatuses = useMatchStatusesForFilter(matchStatus)
  return useCallback(
    <V extends IVariantSummary>(variants: V[]) =>
      stampMatchStatus(
        stampReviews(stampOwnInsightAnswers(variants, variantAnswers), reviews),
        matchStatuses,
      ),
    [variantAnswers, reviews, matchStatuses],
  )
}

export const usePageFilteredDemandVariants = (variants: IVariantSummary[]): IVariantSummary[] => {
  const stamp = useStampDemandPageFields()
  const stamped = useMemo(() => stamp(variants), [stamp, variants])
  return usePageFilteredVariants(stamped)
}
